import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { api, ApiError } from "../lib/api";

export default function NewProjectDialog({ onClose, onCreated }: { onClose: () => void; onCreated: (eventSessionId: number) => void }) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [views, setViews] = useState<string[] | null>(null);
  const [viewsError, setViewsError] = useState<string | null>(null);
  const [participantSourceKey, setParticipantSourceKey] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [createdSessionId, setCreatedSessionId] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    api
      .getSeatplanViews()
      .then((res) => {
        setViews(res.views);
        if (res.views.length > 0) setParticipantSourceKey(res.views[0]);
      })
      .catch((err) => setViewsError(err instanceof ApiError ? err.message : String(err)));
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!file) return;
    setSubmitting(true);
    setError(null);
    setWarnings([]);
    try {
      const result = await api.createProject({ name, participantSourceKey, geometryCsv: file });
      if (result.warnings.length > 0) {
        setWarnings(result.warnings);
        setCreatedSessionId(result.eventSessionId);
      } else {
        onCreated(result.eventSessionId);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  const noConnection = views !== null && views.length === 0 && !viewsError;

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <form className="card dialog-card" onClick={(e) => e.stopPropagation()} onSubmit={handleSubmit}>
        <h2>{t("newProject.heading")}</h2>

        <label className="field">
          {t("newProject.name")}
          <input value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
        </label>

        <label className="field">
          {t("newProject.view")}
          {viewsError ? (
            <span className="error-text">
              {t("newProject.viewsError")} — <Link to="/settings">{t("newProject.goToSettings")}</Link>
            </span>
          ) : noConnection ? (
            <span className="error-text">
              {t("newProject.noViews")} — <Link to="/settings">{t("newProject.goToSettings")}</Link>
            </span>
          ) : (
            <select value={participantSourceKey} onChange={(e) => setParticipantSourceKey(e.target.value)} required disabled={!views}>
              {!views && <option>{t("newProject.loadingViews")}</option>}
              {views?.map((view) => (
                <option key={view} value={view}>
                  {view}
                </option>
              ))}
            </select>
          )}
        </label>

        <label className="field">
          {t("newProject.csv")}
          <input ref={fileInputRef} type="file" accept=".csv,text/csv" onChange={(e) => setFile(e.target.files?.[0] ?? null)} required />
          <span className="newproject-hint">{t("newProject.csvHint")}</span>
        </label>

        {error && <p className="error-text">{error}</p>}
        {warnings.length > 0 && (
          <div className="newproject-warnings">
            <strong>{t("newProject.warningsHeading", { count: warnings.length })}</strong>
            <ul>
              {warnings.slice(0, 10).map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="dialog-actions">
          {createdSessionId !== null ? (
            <button className="btn-primary" type="button" onClick={() => onCreated(createdSessionId)}>
              {t("newProject.continue")}
            </button>
          ) : (
            <>
              <button type="button" onClick={onClose}>
                {t("newProject.cancel")}
              </button>
              <button className="btn-primary" type="submit" disabled={submitting || !file || noConnection || !!viewsError}>
                {submitting ? t("newProject.creating") : t("newProject.create")}
              </button>
            </>
          )}
        </div>
      </form>
    </div>
  );
}
